import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import {
  getStandingsColumns,
  formatStandingsValue,
} from '../../../src/sports/helpers/standingsColumns.helper';
import { TeamCrest } from '../TeamCrest/TeamCrest';
import { getSportStandingsTableStyles } from './SportStandingsTable.styles';
import { SPORT_STANDINGS_TABLE_TEST_IDS } from './SportStandingsTable.constants';

export interface SportStandingsRow {
  teamId: string;
  teamName: string;
  position: number;
  logoUrl?: string | null;
  kit?: { primary?: string; secondary?: string; type?: string } | null;
  /** Column values keyed by the column keys the sport declares. */
  [key: string]: unknown;
}

export interface SportStandingsTableProps {
  rows: SportStandingsRow[];
  /** Sport key from the competition; falls back to football when absent. */
  sport?: string | null;
}

/**
 * Competition standings, with the column SET chosen by the sport (specs/multi-sport-core).
 *
 * A football table and a basketball table are not the same table with different numbers —
 * W/D/L + goals + points versus W/L + win percentage + games behind — so the columns come from
 * `getStandingsColumns` rather than being toggled individually.
 *
 * Deliberately separate from `components/league/LeagueDetailView/LeagueStandingsTable`: that one
 * is football's shipped league table with its own hardcoded header/row components, and this
 * slice does not rewrite it. This table serves the competition shell, where basketball is played.
 */
export const SportStandingsTable: React.FC<SportStandingsTableProps> = ({ rows, sport }) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const styles = useMemo(() => getSportStandingsTableStyles(theme, themeMode), [theme, themeMode]);
  const columns = useMemo(() => getStandingsColumns(sport), [sport]);

  if (rows.length === 0) {
    return (
      <View style={styles.emptyWrap} testID={SPORT_STANDINGS_TABLE_TEST_IDS.empty}>
        <Text style={styles.emptyText}>No standings yet</Text>
      </View>
    );
  }

  return (
    <View style={styles.card} testID={SPORT_STANDINGS_TABLE_TEST_IDS.root}>
      <View style={styles.headerRow} testID={SPORT_STANDINGS_TABLE_TEST_IDS.header}>
        <Text style={[styles.headerCell, styles.positionCell]}>#</Text>
        <View style={styles.teamCell} />
        {columns.map((column) => (
          <Text
            key={column.key}
            style={styles.headerCell}
            accessibilityLabel={column.label}
            testID={`${SPORT_STANDINGS_TABLE_TEST_IDS.headerCellPrefix}${column.key}`}
          >
            {column.shortLabel}
          </Text>
        ))}
      </View>

      {rows.map((row, index) => (
        <View
          key={row.teamId}
          style={[styles.row, index === rows.length - 1 && styles.rowLast]}
          testID={`${SPORT_STANDINGS_TABLE_TEST_IDS.rowPrefix}${row.teamId}`}
        >
          <Text style={[styles.cell, styles.positionCell]}>{row.position}</Text>
          <View style={styles.teamCell}>
            <TeamCrest
              teamName={row.teamName}
              sport={sport}
              logoUrl={row.logoUrl}
              kit={row.kit as never}
              size={22}
            />
            <Text style={styles.teamName} numberOfLines={1}>{row.teamName}</Text>
          </View>
          {columns.map((column) => (
            <Text
              key={column.key}
              style={styles.cell}
              testID={`${SPORT_STANDINGS_TABLE_TEST_IDS.cellPrefix}${row.teamId}-${column.key}`}
            >
              {formatStandingsValue(row[column.key] as number | null | undefined, column.format)}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
};
